import React from "react"
import Layout from "../../components/layout"
import { AppendixWaypoint, Appendix } from "../../components/appendix"
import Hero from "../../components/hero"
import { PageColumns, Column } from "../../components/page-column"
import MobileOnly from "../../components/mobile-only"
import { StatContainer, Stat } from "../../components/stat"
import Slider from "../../components/slider"
import IconGreenHouse from "../../images/who-we-are/csr/icon-greenhouse.inline.svg"
import IconElectricity from "../../images/who-we-are/csr/icon-electricity.inline.svg"
import IconCloud from "../../images/who-we-are/csr/icon-cloud.inline.svg"
import BarChart from "../../components/bar-chart"
import Table from "../../components/table"
import DonationsMatchingData from "../../data/tables/donations-matching.json"

const PAGESLUG = "corporate-social-responsibility"

const stat = (
  <StatContainer classes="stat-container--align-left stat-container--spacing-zero">
    <div className="stat__intro">
      Among the recipients of the Giving Together program in FY 2021
    </div>
    <Stat
      number={380000}
      prefix="$"
      unit="raised in support of critical relief programs on the ground"
    />
    <Stat
      number={535000}
      prefix="$"
      unit="in grants to charities worldwide awarded to"
    />
    <Stat number={28} unit="organizations across" />
    <Stat number={4} unit="continents" />
  </StatContainer>
)

export default function CSR() {
  return (
    <Layout>
      <section className="page page--margin-medium">
        <Hero
          title="Corporate Social Responsibility"
          text="Environmental sustainability and philanthropic initiatives are at the core of the IMF’s corporate social responsibility program. "
          image="who-we-are/csr/hero.jpg"
        />

        <PageColumns>
          <Column type="media">
            <StatContainer classes="stat-container--align-left">
              <Stat
                number={70}
                suffix="%"
                unit="Total greenhouse gas emissions fell compared to 2019"
              >
                <IconGreenHouse />
              </Stat>

              <Stat
                number={35}
                suffix="%"
                unit="Emissions from electricity down due to low building occupancy"
              >
                <IconElectricity />
              </Stat>

              <Stat
                text="Cloud-based IT services"
                unit="led to a permanent reduction in on-premises computing and energy consumption"
              >
                <IconCloud />
              </Stat>
            </StatContainer>
          </Column>

          <Column type="text">
            <h2>Environmental sustainability</h2>
            <p>
              During 2020 the IMF’s primary focus was on helping its members
              deal with the challenges brought by the pandemic, but the
              organization continued to incorporate environmental sustainability
              considerations into its operations. Its total greenhouse gas
              emissions fell by about 70 percent compared with 2019¹, largely
              because of the sharp drop in business travel as a result of the
              pandemic. Emissions from its electricity consumption were down 35
              percent, in large part as a result of lower building occupancy.
              Moreover, the ongoing transition to cloud-based information
              technology services will enable the IMF to reduce on-premises
              computing and energy consumption permanently. The IMF will
              continue to make its operations greener and, in doing so, make
              some of the pandemic-related gains permanent.
            </p>
            <ol className="text-block__footnotes">
              <li>Data is for calendar year 2020.</li>
            </ol>
            <MobileOnly>
              <StatContainer classes="stat-container--align-left">
                <Stat
                  number={70}
                  suffix="%"
                  unit="Total greenhouse gas emissions fell compared to 2019"
                >
                  <IconGreenHouse />
                </Stat>

                <Stat
                  number={35}
                  suffix="%"
                  unit="Emissions from electricity down due to low building occupancy"
                >
                  <IconElectricity />
                </Stat>

                <Stat
                  text="Cloud-based IT services"
                  unit="led to a permanent reduction in on-premises computing and energy consumption"
                >
                  <IconCloud />
                </Stat>
              </StatContainer>
            </MobileOnly>
          </Column>
        </PageColumns>

        {/* Giving Together */}
        <PageColumns>
          <Column type="text">
            <h2>Giving Together</h2>
            <AppendixWaypoint page={PAGESLUG} id={1} />
            <p className="title--r md:title--m" style={{ marginBottom: 20 }}>
              Giving Together, the IMF’s philanthropic program, is supported by
              donations from employees and retirees and funding from the IMF’s
              corporate-giving initiatives.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={1} />
            <p>
              The COVID-19 crisis fundamentally affected the program by
              transforming in-person into virtual interactions and shining a
              spotlight on the immense need both within local communities and
              around the world. Staff and retirees responded with unprecedented
              support. Total funding from employee and retiree donations, Giving
              Together grants, and IMF corporate matching and donations resulted
              in the program providing more than $4.6 million to charitable
              causes in FY 2021—the largest amount ever.
            </p>

            <h3>Giving</h3>
            <p>
              This year’s giving campaign raised a total of $2.8 million to
              support organizations in the Washington, DC, metro area and across
              the globe, representing a 21 percent year-over-year increase and
              shattering records for both dollars raised and staff
              participation. In addition, the Giving Together program organized
              fundraisers for relief efforts for disasters in Cambodia, El
              Salvador, Guatemala, Honduras, India, Lebanon, Nicaragua, the
              Philippines, St. Vincent and the Grenadines, Thailand, and
              Vietnam, with more than $380,000 raised in support of critical
              relief programs on the ground.
            </p>
            <p>
              In total, employee and retiree donations, plus matching funds,
              raised $4.1 million to support charitable causes and humanitarian
              relief efforts during FY 2021—exceeding the $3.6 million raised
              the previous year.
            </p>
            <MobileOnly>
              <BarChart
                id="donations-matching"
                figureNumber="3.2"
                title="Total Raised in Donations and Matching Funds"
                subTitle="(millions of US dollars)"
              >
                <Table
                  data={DonationsMatchingData}
                  figureNumber="3.2"
                  title="Total Raised in Donations and Matching Funds"
                  subTitle="(millions of US dollars)"
                />
              </BarChart>

              {stat}
            </MobileOnly>

            <h3>Grants</h3>
            <p>
              The IMF also awarded $535,000 in grants to charities worldwide in
              FY 2021. Given the ongoing impact of COVID-19 on nonprofits and
              the communities they serve, many of this year’s grants were
              awarded to programs and services that have specific operational
              support requirements owing to the COVID-19 crisis or support
              groups disproportionately affected by COVID-19. This included
              exceptional grants to nonprofits working to advance issues of
              racial and social justice in the United States, and matching
              grants to initiatives led by the IMF Staff Association for the WHO
              and for hospitality workers impacted by the crisis. In total,
              grants were awarded to 28 organizations across four continents.
            </p>

            <h3>Volunteerism</h3>
            <p>
              To support their local communities, even—and especially—against
              the backdrop of COVID-19, the IMF staff found new, creative ways
              to make an impact through volunteerism. The Giving Together
              program launched a photo contest in conjunction with this year’s
              giving campaign, and staff members shared entries that highlighted
              the breadth of their activities—preparing healthy meals for hungry
              families, volunteering with their local libraries, supporting
              elderly neighbors in need, and completing socially distanced
              fitness challenges for a range of causes.
            </p>
            <MobileOnly>
              <Slider id="csr" />
            </MobileOnly>
            <p>
              In addition, in honor of Martin Luther King Jr. Day, staff members
              participated in a virtual volunteering event in partnership with
              Free Minds Book Club & Writing Workshop, a Washington, DC,
              nonprofit that helps incarcerated youth through workforce
              development, violence prevention, and promotion of the literary
              arts.
            </p>
          </Column>

          <Column type="media" classes="page-columns__column--space-around">
            <BarChart
              id="donations-matching"
              figureNumber="3.2"
              title="Total Raised in Donations and Matching Funds"
              subTitle="(millions of US dollars)"
            >
              <Table
                data={DonationsMatchingData}
                figureNumber="3.2"
                title="Total Raised in Donations and Matching Funds"
                subTitle="(millions of US dollars)"
              />
            </BarChart>

            {stat}

            <Slider id="csr" />
          </Column>
        </PageColumns>
      </section>
      <Appendix page={PAGESLUG} />
    </Layout>
  )
}
